import React, {
  FormEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { EntityDiffSummary } from '@trustero/trustero-api-web/lib/contentversion/contentversion_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { StyledTextInput } from 'src/components/Reusable/Inputs/TextInput/styles'
import { NewMarkdownEditor } from 'src/components/Reusable/Inputs/NewMarkdownEditor'
import { RadioInput } from 'src/components/Reusable/Inputs/Radio'
import { BoxLoader } from 'src/components/Reusable/ContentLoaders/BoxLoader'
import {
  PublishContentVersionArgs,
  usePreviewContentVersion,
  usePublishContentVersion,
} from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { ERROR_MESSAGES } from 'src/Utils/globalEnums'
import { CompactMarkdown } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.styles'
import { ModalForm, ModalFormId } from '../..'
import {
  StyledComplianceFrameworkInputContainer,
  StyledFieldLabel,
} from '../AddFramework/ConfirmFrameworkModal.styles'
import {
  DiffGrid,
  DiffHeader,
  DiffEntityLabel,
  DiffCount,
  FieldLabel,
  NumberInput,
  VersionInputRow,
  VersionPreview,
} from './PublishContentVersionModal.styles'

const TITLE_MAX = 120

type EntityRow = {
  label: string
  summary?: EntityDiffSummary
}

const summarize = (s?: EntityDiffSummary) => ({
  added: s?.getAdded() ?? 0,
  modified: s?.getModified() ?? 0,
  removed: s?.getRemoved() ?? 0,
})

const parseNonNegativeInt = (value: string): number => {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0
}

const DiffRow = ({ row }: { row: EntityRow }): JSX.Element => {
  const { added, modified, removed } = summarize(row.summary)
  return (
    <>
      <DiffEntityLabel>{row.label}</DiffEntityLabel>
      <DiffCount>{added}</DiffCount>
      <DiffCount>{modified}</DiffCount>
      <DiffCount>{removed}</DiffCount>
    </>
  )
}

export const PublishContentVersionModal = (): JSX.Element => {
  const show = useIsShowModal(ModalFormId.PUBLISH_CONTENT_VERSION)
  const hide = useHideModal({ modalId: ModalFormId.PUBLISH_CONTENT_VERSION })
  const {
    data: preview,
    loading: previewLoading,
    error: previewError,
  } = usePreviewContentVersion(show)
  const publish = usePublishContentVersion()

  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState('')
  const [changelog, setChangelog] = useState('')
  const [bumpKind, setBumpKind] = useState<'minor' | 'major'>('minor')
  const [initialMajor, setInitialMajor] = useState(0)
  const [initialMinor, setInitialMinor] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const didInitRef = useRef(false)
  useEffect(() => {
    if (!show) {
      didInitRef.current = false
      return
    }
    if (!preview || didInitRef.current) return
    didInitRef.current = true
    setChangelog(preview.getSuggestedChangelog())
    setTitle('')
    setTitleError('')
    setBumpKind('minor')
    setInitialMajor(0)
    setInitialMinor(0)
  }, [show, preview])

  useEffect(() => {
    if (!previewError) return
    const errorMessage = getErrorMessage(previewError)
    toast.error(errorMessage.message)
    if (errorMessage.shouldLog) {
      log.error('PublishContentVersionModal preview failed', previewError)
    }
    hide()
  }, [previewError, hide])

  const isFirstPublish = preview?.getIsFirstPublish() ?? false
  const latestMajor = preview?.getLatestMajorVersion() ?? 0
  const latestMinor = preview?.getLatestMinorVersion() ?? 0

  const nextVersionLabel = useMemo(() => {
    if (!preview) return ''
    if (isFirstPublish) return `${initialMajor}.${initialMinor}`
    if (bumpKind === 'major') return `${latestMajor + 1}.0`
    return `${latestMajor}.${latestMinor + 1}`
  }, [
    preview,
    isFirstPublish,
    bumpKind,
    latestMajor,
    latestMinor,
    initialMajor,
    initialMinor,
  ])

  const diffRows: EntityRow[] = useMemo(
    () => [
      { label: 'Frameworks', summary: preview?.getFrameworks() },
      { label: 'Sections', summary: preview?.getFrameworkSections() },
      { label: 'Requirements', summary: preview?.getFrameworkRequirements() },
      { label: 'Controls', summary: preview?.getControls() },
      { label: 'Policies', summary: preview?.getPolicies() },
      { label: 'Test procedures', summary: preview?.getTestProcedures() },
      { label: 'Departments', summary: preview?.getDepartments() },
    ],
    [preview],
  )

  const closeAndReset = () => {
    setTitle('')
    setTitleError('')
    setChangelog('')
    setBumpKind('minor')
    setInitialMajor(0)
    setInitialMinor(0)
    hide()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!title.trim()) {
      setTitleError(ERROR_MESSAGES.EMPTY)
      return
    }
    setTitleError('')
    setIsSubmitting(true)
    try {
      const args: PublishContentVersionArgs = {
        isMajor: !isFirstPublish && bumpKind === 'major',
        title: title.trim(),
        changelog,
      }
      if (isFirstPublish) {
        args.initialMajorVersion = initialMajor
        args.initialMinorVersion = initialMinor
      }
      const record = await publish(args)
      toast.success(
        `Published version ${record.getMajorVersion()}.${record.getMinorVersion()}.`,
      )
      closeAndReset()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('PublishContentVersionModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.PUBLISH_CONTENT_VERSION}
      size="lg"
      title="Publish content version"
      submitText="Publish"
      disableSubmitButton={isSubmitting || previewLoading || !preview}
    >
      <form id={ModalFormId.PUBLISH_CONTENT_VERSION} onSubmit={onSubmit}>
        <FlexColumn gap={20} align={FlexAlign.FLEX_START} isFullWidth>
          {previewLoading ? (
            <BoxLoader />
          ) : preview ? (
            <>
              <StyledComplianceFrameworkInputContainer>
                <p>Changes since last published version</p>
                <DiffGrid>
                  <DiffHeader>Entity</DiffHeader>
                  <DiffHeader>Added</DiffHeader>
                  <DiffHeader>Modified</DiffHeader>
                  <DiffHeader>Removed</DiffHeader>
                  {diffRows.map((row) => (
                    <DiffRow key={row.label} row={row} />
                  ))}
                </DiffGrid>
              </StyledComplianceFrameworkInputContainer>

              <StyledComplianceFrameworkInputContainer>
                <p>Version</p>
                {isFirstPublish ? (
                  <VersionInputRow
                    align={FlexAlign.CENTER}
                    justify={FlexAlign.FLEX_START}
                  >
                    <FieldLabel htmlFor="publish-initial-major">
                      Major
                    </FieldLabel>
                    <NumberInput
                      id="publish-initial-major"
                      type="number"
                      min={0}
                      value={initialMajor}
                      onChange={(e) =>
                        setInitialMajor(parseNonNegativeInt(e.target.value))
                      }
                    />
                    <FieldLabel htmlFor="publish-initial-minor">
                      Minor
                    </FieldLabel>
                    <NumberInput
                      id="publish-initial-minor"
                      type="number"
                      min={0}
                      value={initialMinor}
                      onChange={(e) =>
                        setInitialMinor(parseNonNegativeInt(e.target.value))
                      }
                    />
                    <VersionPreview>
                      Publishing as {nextVersionLabel}
                    </VersionPreview>
                  </VersionInputRow>
                ) : (
                  <VersionInputRow
                    align={FlexAlign.CENTER}
                    justify={FlexAlign.FLEX_START}
                  >
                    <RadioInput
                      id="publish-bump-major"
                      name="publish-bump-kind"
                      label="Major"
                      checked={bumpKind === 'major'}
                      onChange={() => setBumpKind('major')}
                    />
                    <RadioInput
                      id="publish-bump-minor"
                      name="publish-bump-kind"
                      label="Minor"
                      checked={bumpKind === 'minor'}
                      onChange={() => setBumpKind('minor')}
                    />
                    <VersionPreview>
                      Publishing as {nextVersionLabel}
                    </VersionPreview>
                  </VersionInputRow>
                )}
              </StyledComplianceFrameworkInputContainer>

              <StyledComplianceFrameworkInputContainer>
                <p>Title</p>
                <StyledTextInput
                  value={title}
                  showError={!!titleError}
                  maxLength={TITLE_MAX}
                  placeholder="Short headline for this version"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (titleError) setTitleError('')
                    setTitle(e.target.value)
                  }}
                />
                <StyledFieldLabel
                  validateForm={!!titleError}
                  isInvalid={!!titleError}
                  message={titleError}
                />
              </StyledComplianceFrameworkInputContainer>

              <StyledComplianceFrameworkInputContainer>
                <p>Changelog</p>
                <CompactMarkdown>
                  <NewMarkdownEditor
                    value={changelog}
                    onChange={setChangelog}
                    placeholder="Describe what changed in this version"
                  />
                </CompactMarkdown>
              </StyledComplianceFrameworkInputContainer>
            </>
          ) : null}
        </FlexColumn>
      </form>
    </ModalForm>
  )
}
